import React, { useState, useEffect } from 'react';
// [Web UX 3. csomag (D), 2026-10-04] A kérés-hibák szövege a közös `apiErrorMessage`-en át megy (a szerver magyar
// indoklása mindhárom törzs-alakból / hálózati hibánál magyar mondat / különben „<tartalék> (HTTP n)"),
// a nyers `err.response?.data?.message` helyett. Őr: `utils/__tests__/rawErrorMessages.guard.test.ts`.
import { apiErrorMessage } from '../utils/apiErrors';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
// [Web UX 9 / T2, 2026-10-13] a dátumválasztók magyar feliratai (Cancel/OK/Select date helyett) — lásd utils/dateLocale.ts
import { HU_PICKER_LOCALE_TEXT } from '../utils/dateLocale';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/hu';
import { useAuth } from '../contexts/AuthContext';
import trainingBalanceService from '../services/trainingBalanceService';
import { CoachStatistics as CoachStatisticsType, CoachSession } from '../types/trainingBalance';
import { PaymentStatus, PaymentStatusDisplayNames, PaymentStatusColors } from '../types/coachPayment';
// Óraszám MINDIG magyar tizedesvesszővel („1,5"), a jelenléti ív / CoachPresenceControl és a
// mobil alakjával egyezően. A korábbi `toFixed(1)` angol pontot adott („1.5") — ugyanaz az
// óraszám két helyen kétféleképp jelent meg.
import { formatHours } from '../utils/coachPresence';

dayjs.locale('hu');

const CoachStatistics: React.FC = () => {
  const { user } = useAuth();
  const [statistics, setStatistics] = useState<CoachStatisticsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<Dayjs>(dayjs().subtract(4, 'month'));
  const [endDate, setEndDate] = useState<Dayjs>(dayjs());

  useEffect(() => {
    loadStatistics();
  }, [startDate, endDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadStatistics = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      const result = await trainingBalanceService.getCoachStatistics(
        startDate.toDate(),
        endDate.toDate(),
        true
      );
      setStatistics(result);
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Hiba történt a statisztikák betöltésekor'));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number | null) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: 'HUF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!statistics) {
    return <Alert severity="info">Nincs elérhető statisztika az adott időszakra.</Alert>;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="hu" localeText={HU_PICKER_LOCALE_TEXT}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Saját statisztikáim
        </Typography>

        {/* Date Range Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <DatePicker
                  label="Időszak kezdete"
                  value={startDate}
                  onChange={(newValue) => {
                    if (newValue) {
                      setStartDate(dayjs(newValue));
                    }
                  }}
                  format="YYYY.MM.DD"
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <DatePicker
                  label="Időszak vége"
                  value={endDate}
                  onChange={(newValue) => {
                    if (newValue) {
                      setEndDate(dayjs(newValue));
                    }
                  }}
                  format="YYYY.MM.DD"
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Statistics Cards */}
        <Grid container spacing={3}>
          {/* Sessions */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Tartott edzések
                </Typography>
                <Typography variant="h4">{statistics.attendedSessions}</Typography>
                <Typography variant="body2" color="textSecondary">
                  Összes: {statistics.totalSessions}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Hours */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Óraszám összesen
                </Typography>
                <Typography variant="h4">{formatHours(statistics.totalHours)} óra</Typography>
                <Typography variant="body2" color="textSecondary">
                  Csak a megtartott edzéseken
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Compensation */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Kalkulált díjazás
                </Typography>
                <Typography variant="h4">
                  {formatCurrency(statistics.calculatedCompensation)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {statistics.hourlyRate ? `${formatCurrency(statistics.hourlyRate)}/óra` : 'Óradíj nincs beállítva'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Attendance Rate */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Átlagos jelenlét
                </Typography>
                <Typography variant="h4">{statistics.averageAttendanceRate.toFixed(1)}%</Typography>
                <Typography variant="body2" color="textSecondary">
                  Saját edzéseken
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Collected */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Beszedett összeg
                </Typography>
                <Typography variant="h4">{formatCurrency(statistics.totalCollected ?? 0)}</Typography>
                <Typography variant="body2" color="textSecondary">
                  Helyszíni fizetés
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Amount Due */}
          {statistics.amountDue !== null && statistics.amountDue !== undefined && (
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Fizetendő
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {formatCurrency(statistics.amountDue)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Díjazás - beszedett
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Training Paid Out */}
          {statistics.trainingPaidOut > 0 && (
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Kifizetett (edzés)
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {formatCurrency(statistics.trainingPaidOut)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Edzés díjazás + levonások
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Extra Paid Out */}
          {statistics.extraPaidOut > 0 && (
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Egyéb kifizetések
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#7c3aed' }}>
                    {formatCurrency(statistics.extraPaidOut)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Útiköltség, bónusz, egyéb
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Balance */}
          {statistics.balance !== null && statistics.balance !== undefined && (
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Egyenleg
                  </Typography>
                  <Typography
                    variant="h4"
                    color={statistics.balance > 0 ? 'warning.main' : statistics.balance < 0 ? 'success.main' : 'text.primary'}
                  >
                    {formatCurrency(statistics.balance)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {statistics.balance > 0 ? 'Hátralék' : statistics.balance < 0 ? 'Túlfizetés' : 'Rendben'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>

        {/* Summary Card */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Összefoglaló
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="textSecondary">
                  Időszak
                </Typography>
                <Typography variant="body1">
                  {dayjs(statistics.periodStart).format('YYYY.MM.DD')} - {dayjs(statistics.periodEnd).format('YYYY.MM.DD')}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="textSecondary">
                  Összes résztvevő
                </Typography>
                <Typography variant="body1">{statistics.totalParticipants} fő</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="textSecondary">
                  Edzések, ahol jelen voltál
                </Typography>
                <Typography variant="body1">
                  {statistics.attendedSessions} / {statistics.totalSessions} ({statistics.totalSessions > 0 ? ((statistics.attendedSessions / statistics.totalSessions) * 100).toFixed(1) : 0}%)
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="textSecondary">
                  Átlagos óraszám/hét
                </Typography>
                <Typography variant="body1">
                  {formatHours((statistics.totalHours / dayjs(statistics.periodEnd).diff(dayjs(statistics.periodStart), 'week', true)) || 0)} óra
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Session Breakdown */}
        {statistics.sessionBreakdown && statistics.sessionBreakdown.length > 0 && (
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Edzés részletező ({statistics.sessionBreakdown.length} db)
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Dátum</TableCell>
                      <TableCell>Esemény</TableCell>
                      <TableCell>Időpont</TableCell>
                      <TableCell align="right">Órák</TableCell>
                      <TableCell>Jelenlét</TableCell>
                      <TableCell align="right">Résztvevők</TableCell>
                      <TableCell>Kifizetés</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {statistics.sessionBreakdown.map((session) => {
                      const paymentInfo = session.paymentStatus
                        ? {
                            label: PaymentStatusDisplayNames[session.paymentStatus as PaymentStatus],
                            color: PaymentStatusColors[session.paymentStatus as PaymentStatus] as 'success' | 'warning' | 'info' | 'default',
                          }
                        : null;
                      return (
                        <TableRow key={`${session.eventId}-${session.eventDate}`}>
                          <TableCell>{dayjs(session.eventDate).format('MM.DD. (dd)')}</TableCell>
                          <TableCell>{session.eventName}</TableCell>
                          <TableCell>{session.startTime?.substring(0, 5)} - {session.endTime?.substring(0, 5)}</TableCell>
                          <TableCell align="right">
                            {formatHours(session.hours)}
                            {session.coachAttended && session.eventHours != null && session.hours < session.eventHours && (
                              <Chip size="small" color="warning" variant="outlined" label={`részleges · ${formatHours(session.eventHours)} órából`} sx={{ ml: 1 }} />
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={session.coachAttended ? 'Jelen' : 'Hiányzott'}
                              color={session.coachAttended ? 'success' : 'error'}
                            />
                          </TableCell>
                          <TableCell align="right">
                            {session.attendedCount}/{session.participantCount}
                          </TableCell>
                          <TableCell>
                            {session.isPaid && paymentInfo ? (
                              <Chip size="small" label={paymentInfo.label} color={paymentInfo.color} />
                            ) : (
                              <Chip size="small" label="Kifizetetlen" variant="outlined" />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {!statistics.hourlyRate && (
          <Alert severity="info" sx={{ mt: 3 }}>
            Az óradíj nincs beállítva a profilodban. Kérd meg az adminisztrátort, hogy állítsa be a díjazásodat.
          </Alert>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default CoachStatistics;
